import type { ReactNode } from "react"

import { useNavigate, useParams } from "react-router-dom"

import type {
  SolarProposalFinancingDto,
  SolarQuoteDto,
} from "~/api/dto/solar-proposal-financing.dto"
import useSolarProposalFinancing from "~/api/hooks/useSolarProposalFinancing"
import { Drawer } from "~/components/ui-components/v2"

import type { SolarLender } from "./solar-finance-partner"
import { solarLenderOf } from "./solar-finance-partner"
import {
  SOLAR_REFRESH_REQUIRED_PROMPT,
  solarCreditHumanBundle,
} from "./solar-financing-bundle"
import { LIGHTREACH_FINANCING_STATUS_TITLE } from "./solar-lightreach-copy"
import {
  SOLAR_SETUP_DRAWER_TITLES,
  solarSetupGroupForStep,
  solarSetupGroupPath,
} from "./solar-setup-paths"
import { SolarConcertFinancingStatus } from "./SolarConcertFinancingStatus"
import { SolarLightReachFinancingStatus } from "./SolarLightReachFinancingStatus"
import { SolarRefreshPricingFix } from "./SolarRefreshPricingFix"
import { SolarSungageFinancingStatus } from "./SolarSungageFinancingStatus"
import { SolarSungageQuoteCard } from "./SolarSungageQuoteCard"
import {
  PAYMENT_OUT_OF_DATE_LABEL,
  PAYMENT_UNAVAILABLE_LABEL,
} from "../ProposalSetup/financing/CatalogLoanProductPicker"
import { CreditHumanFinancingStatus } from "../ProposalSetup/financing/CreditHumanFinancingStatus"

export const FINANCING_STATUS_NO_LOAN_PRODUCT_PROMPT = `Financing applications are only for Credit Human, Concert, Sungage or LightReach financing.`
export const FINANCING_STATUS_LOAD_ERROR = `Unable to load the financing.`
export const CREDIT_HUMAN_FINANCING_STATUS_TITLE = `Credit Human Financing Status`
export const CONCERT_FINANCING_STATUS_TITLE = `Concert Financing Status`
export const SUNGAGE_FINANCING_STATUS_TITLE = `Sungage Financing Status`
export const NO_QUOTE_PROMPT = `No payment yet. Choose a loan term and Refresh Pricing to calculate it.`

/** The drawer names the selected partner. */
const FINANCING_STATUS_TITLES: Record<SolarLender, string> = {
  credit_human: CREDIT_HUMAN_FINANCING_STATUS_TITLE,
  concert: CONCERT_FINANCING_STATUS_TITLE,
  sungage: SUNGAGE_FINANCING_STATUS_TITLE,
  lightreach: LIGHTREACH_FINANCING_STATUS_TITLE,
}

const usd = new Intl.NumberFormat(`en-US`, {
  style: `currency`,
  currency: `USD`,
})

/** The Quote's figures as the gateway sent them; nothing is computed here. */
const QuoteSummary = ({
  quote,
  testId,
}: {
  quote: SolarQuoteDto | null
  testId: string
}): JSX.Element => {
  if (quote == null) {
    return (
      <p className="text-[length:var(--step--1)] text-bp-text-muted">
        {NO_QUOTE_PROMPT}
      </p>
    )
  }
  const product = quote.loanProduct
  return (
    <dl
      data-testid={testId}
      className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-2 rounded-lg border border-bp-border bg-bp-elevated px-4 py-3 text-[length:var(--step--2)]"
    >
      <dt className="text-bp-text-dim">Loan Product</dt>
      <dd className="text-bp-text">{product?.name ?? `—`}</dd>
      <dt className="text-bp-text-dim">Term</dt>
      <dd className="text-bp-text">
        {product != null ? `${product.termYears}-year` : `—`}
      </dd>
      <dt className="text-bp-text-dim">APR</dt>
      <dd className="text-bp-text">
        {product?.apr != null ? `${product.apr.toFixed(2)}%` : `—`}
      </dd>
      <dt className="text-bp-text-dim">Financed amount</dt>
      <dd className="text-bp-text tabular-nums">
        {quote.loanAmount != null ? usd.format(quote.loanAmount) : `—`}
      </dd>
      <dt className="text-bp-text-dim">Monthly payment</dt>
      <dd className="text-bp-text tabular-nums">
        {quote.estimatedMonthlyPayment == null ? (
          <span className="text-bp-danger">
            {quote.unavailableReason
              ? `${PAYMENT_UNAVAILABLE_LABEL}: ${quote.unavailableReason}`
              : PAYMENT_UNAVAILABLE_LABEL}
          </span>
        ) : (
          <>
            {`${usd.format(quote.estimatedMonthlyPayment)}/mo`}
            {quote.status === `stale` ? (
              <span className="ml-2 text-bp-gold">
                {PAYMENT_OUT_OF_DATE_LABEL}
              </span>
            ) : null}
          </>
        )}
      </dd>
    </dl>
  )
}

type LoanLender = Exclude<SolarLender, `lightreach`>

const QUOTE_SUMMARY_TEST_IDS: Record<LoanLender, string> = {
  credit_human: `solar-credit-human-quote`,
  concert: `solar-concert-quote`,
  sungage: `solar-sungage-quote-summary`,
}

/**
 * Credit Human's application: why Apply waits, when the read says so, above
 * the shared status and actions.
 */
const CreditHumanApplication = ({
  proposalId,
  financing,
}: {
  proposalId: string
  financing: SolarProposalFinancingDto
}): JSX.Element => (
  <>
    {financing.refreshPricingRequired ? (
      // The read says why Apply and Replace wait, so the fix stays
      // beside it after a refusal, or with a Quote stale on arrival.
      <div
        role="status"
        data-testid="solar-financing-refresh-required"
        className="flex flex-col items-start gap-2"
      >
        <p className="text-[length:var(--step--2)] text-bp-gold">
          {SOLAR_REFRESH_REQUIRED_PROMPT}
        </p>
        <SolarRefreshPricingFix
          proposalId={proposalId}
          onRefreshed={() => undefined}
        />
      </div>
    ) : !financing.actions.apply.allowed &&
      financing.currentApplication == null &&
      financing.actions.apply.reason ? (
      <p className="text-[length:var(--step--2)] text-bp-text-muted">
        {financing.actions.apply.reason}
      </p>
    ) : null}
    <CreditHumanFinancingStatus
      bundle={solarCreditHumanBundle}
      proposalId={proposalId}
      financing={financing}
    />
  </>
)

const LoanApplication = ({
  lender,
  proposalId,
  financing,
}: {
  lender: LoanLender
  proposalId: string
  financing: SolarProposalFinancingDto
}): JSX.Element => {
  switch (lender) {
    case `sungage`:
      return (
        <>
          <SolarSungageQuoteCard quote={financing.quote} />
          <SolarSungageFinancingStatus proposalId={proposalId} />
        </>
      )
    case `concert`:
      return (
        <SolarConcertFinancingStatus
          proposalId={proposalId}
          financing={financing}
        />
      )
    case `credit_human`:
      return (
        <CreditHumanApplication proposalId={proposalId} financing={financing} />
      )
  }
}

/**
 * A loan plan's status: the lock, the Quote an application consumes, then the
 * lender's application. Credit Human: the shared Existing Loan Application
 * status and actions over Solar's commands; approval locks the selections
 * until Replace. Concert: the shared Concert application, stipulations and
 * loan documents. Sungage: its quote, then the shared Sungage
 * hosted-application status.
 */
const LoanFinancingStatus = ({
  lender,
  proposalId,
  financing,
}: {
  lender: LoanLender
  proposalId: string
  financing: SolarProposalFinancingDto
}): JSX.Element => (
  <>
    {financing.financingLocked && financing.financingLockReason ? (
      <p role="status" className="text-[length:var(--step--2)] text-bp-gold">
        {financing.financingLockReason}
      </p>
    ) : null}
    <QuoteSummary
      quote={financing.quote}
      testId={QUOTE_SUMMARY_TEST_IDS[lender]}
    />
    <LoanApplication
      lender={lender}
      proposalId={proposalId}
      financing={financing}
    />
  </>
)

/**
 * The plan's financing status. LightReach: the selected offer, the shared
 * LightReach application, status and contract; a TPO has no loan Quote to
 * summarize. Every other lender: its Quote and application.
 */
export const SolarFinancingStatusStep = ({
  proposalId,
  onClose,
  children = null,
}: {
  proposalId: string
  onClose: () => void
  /** Above the status: the Contract route's link to the supplemental package. */
  children?: ReactNode
}): JSX.Element => {
  const financingQuery = useSolarProposalFinancing(proposalId)
  const financing = financingQuery.data ?? null
  // The read requires a Loan Product exactly when the plan names a lender.
  const lender = financing?.loanProductRequired ? solarLenderOf(financing) : null
  // The selected partner names the drawer; a plan without one keeps the rail's label.
  const title =
    lender == null
      ? SOLAR_SETUP_DRAWER_TITLES[`financing-status`]
      : FINANCING_STATUS_TITLES[lender]

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title={title}
      widthClass="sm:max-w-lg"
    >
      <div
        className="flex flex-col gap-4"
        data-testid="solar-financing-status-step"
      >
        {children}
        {financingQuery.isError ? (
          <p
            role="alert"
            className="text-[length:var(--step--1)] text-bp-danger"
          >
            {FINANCING_STATUS_LOAD_ERROR}
          </p>
        ) : financing == null ? null : lender == null ? (
          <p className="text-[length:var(--step--1)] text-bp-text-muted">
            {FINANCING_STATUS_NO_LOAN_PRODUCT_PROMPT}
          </p>
        ) : lender === `lightreach` ? (
          // A TPO has no loan Quote; the LightReach read states its own lock.
          <SolarLightReachFinancingStatus proposalId={proposalId} />
        ) : (
          <LoanFinancingStatus
            lender={lender}
            proposalId={proposalId}
            financing={financing}
          />
        )}
      </div>
    </Drawer>
  )
}

/**
 * Financing Status from its route, as Contract status is, so a read-only
 * viewer can open it too: the status shows only the actions the read allows.
 * Closing returns to the step's group, which sends a read-only viewer back to
 * the proposal.
 */
export const SolarFinancingStatusRoute = (): JSX.Element => {
  const { proposalId = `` } = useParams<{ proposalId: string }>()
  const navigate = useNavigate()
  return (
    <SolarFinancingStatusStep
      proposalId={proposalId}
      onClose={() =>
        navigate(
          solarSetupGroupPath(
            proposalId,
            solarSetupGroupForStep(`financing-status`).id,
          ),
        )
      }
    />
  )
}
